import React from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";

const RelatedApartments = ({ apartments = [], viewAllPath = "/apartmani" }) => {
  const navigate = useNavigate();

  const handleViewApartment = (apt) => {
    // Prefer explicit link; fallback to first apartment page
    const to = apt?.link || "/apartmani/apartment-1";
    navigate(to);
  };

  const handleViewAll = () => {
    navigate(viewAllPath);
  };

  if (!apartments?.length) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
          More Apartments You Might Like
        </h2>
        <Button
          variant="outline"
          iconName="ArrowRight"
          iconPosition="right"
          onClick={handleViewAll}
        >
          View All
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {apartments.map((apartment, idx) => {
          const img = apartment?.imageUrl || apartment?.image || ""; // tolerate both keys

          return (
            <div
              key={apartment?.id ?? idx}
              className="bg-card rounded-xl shadow-subtle hover:shadow-medium transition-smooth overflow-hidden group cursor-pointer"
              onClick={() => handleViewApartment(apartment)}
            >
              {/* Image */}
              <div className="relative w-full aspect-[16/9] overflow-hidden bg-muted">
                {img ? (
                  <img
                    src={img}
                    alt={apartment?.title || "Apartment"}
                    className="w-full h-full object-cover group-hover:scale-105 transition-smooth"
                    onError={(e) => {
                      e.currentTarget.src =
                        "https://placehold.co/800x450?text=Photo+unavailable";
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                    No image
                  </div>
                )}

                <div className="absolute top-4 left-4">
                  <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-medium">
                    {apartment?.status || "Available"}
                  </span>
                </div>

                <div className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-smooth opacity-0 group-hover:opacity-100">
                  <Icon name="Eye" size={16} />
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-heading text-xl font-semibold text-foreground group-hover:text-primary transition-smooth">
                    {apartment?.title}
                  </h3>
                  <div className="text-right">
                    {apartment?.price != null && (
                      <>
                        <p className="font-heading text-lg font-bold text-primary">
                          €{apartment.price}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          per night
                        </p>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                  <div className="flex items-center space-x-1">
                    <Icon name="Users" size={14} />
                    <span>
                      {apartment?.capacity
                        ? `${apartment.capacity} guests`
                        : "guests"}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="MapPin" size={14} />
                    <span>{apartment?.location || "Rijeka, Croatia"}</span>
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  iconName="Eye"
                  iconPosition="left"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewApartment(apartment);
                  }}
                >
                  View Details
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* CTA */}
      <div className="text-center mt-8 p-8 bg-accent/5 rounded-xl border border-accent/20">
        <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
          Can't find what you're looking for?
        </h3>
        <p className="text-muted-foreground mb-4">
          Browse our complete collection of apartments and rooms for more
          options.
        </p>
        <Button
          size="lg"
          iconName="Search"
          iconPosition="left"
          onClick={handleViewAll}
        >
          Browse All Properties
        </Button>
      </div>
    </div>
  );
};

export default RelatedApartments;
